import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Select from '@components/select';
import Button from '@components/button';

import styles from './styles.module.scss';

const {
  container,
  calculator_header,
  calculator_selector,
  calculator_content,
  calculator_left,
  calculator_right,
  form_section,
  form_inputs,
  grid_layout,
  form_actions,
  results_section,
  disclaimer_section,
  disclaimer_header,
  disclaimer_content,
  condensed,
  hide_visual
} = styles;

const CalculatorLayout = ({
  title,
  description,
  calculatorType,
  calculatorOptions = [],
  onCalculatorChange,
  children,
  resultsContent,
  disclaimerContent,
  showCalculatorSelector = true,
  onCalculate,
  calculateButtonLabel = 'Calculate',
  hideVisual: hideVisualProp = false,
  condensed: condensedProp = false,
  layout = 'balanced',
  gridLayout = false,
  className
}) => {
  const [selectedCalculator, setSelectedCalculator] = useState(calculatorType);
  const [disclaimerExpanded, setDisclaimerExpanded] = useState(false);

  const handleCalculatorChange = (value) => {
    setSelectedCalculator(value);
    if (onCalculatorChange) {
      onCalculatorChange(value);
    }
  };

  const classes = classNames(container, {
    [className]: className,
    [condensed]: condensedProp
  });

  const headerClasses = classNames(calculator_header, {
    [condensed]: condensedProp
  });

  const contentClasses = classNames(calculator_content, {
    [condensed]: condensedProp,
    [styles[`layout_${layout.replace(/-/g, '_')}`]]: layout !== 'balanced'
  });

  const leftClasses = classNames(calculator_left, {
    [condensed]: condensedProp
  });

  const rightClasses = classNames(calculator_right, {
    [hide_visual]: hideVisualProp,
    [condensed]: condensedProp
  });

  const HeadingTag = condensedProp ? 'span' : 'h1';

  const content = (
    <div className={classes}>
      <div className={headerClasses}>
        {showCalculatorSelector && calculatorOptions.length > 0 && (
          <div className={calculator_selector}>
            <Select
              id="calculator-selector"
              label="Select a calculator"
              options={calculatorOptions}
              selected={selectedCalculator}
              onChange={handleCalculatorChange}
            />
          </div>
        )}
        <HeadingTag>{title}</HeadingTag>
        {description && <p>{description}</p>}
      </div>

      <div className={contentClasses}>
        <div className={leftClasses}>
          <div className={form_section}>
            <div className={classNames(form_inputs, { [grid_layout]: gridLayout })}>{children}</div>
            <div className={form_actions}>
              {onCalculate && (
                <Button
                  type="button"
                  label={calculateButtonLabel}
                  onClick={onCalculate}
                  theme="red"
                />
              )}
            </div>
          </div>
          {disclaimerContent && (
            <div className={disclaimer_section}>
              <div
                className={disclaimer_header}
                onClick={() => setDisclaimerExpanded(!disclaimerExpanded)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDisclaimerExpanded(!disclaimerExpanded);
                  }
                }}
              >
                LEGAL DISCLAIMERS
                <span className={disclaimerExpanded ? 'expanded' : ''}>
                  {disclaimerExpanded ? '▲' : '▼'}
                </span>
              </div>
              {disclaimerExpanded && <div className={disclaimer_content}>{disclaimerContent}</div>}
            </div>
          )}
        </div>

        <div className={rightClasses}>
          <div className={results_section}>{resultsContent}</div>
        </div>
      </div>
    </div>
  );

  return content;
};

CalculatorLayout.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  calculatorType: PropTypes.string,
  calculatorOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  onCalculatorChange: PropTypes.func,
  children: PropTypes.node.isRequired,
  resultsContent: PropTypes.node,
  disclaimerContent: PropTypes.node,
  showCalculatorSelector: PropTypes.bool,
  onCalculate: PropTypes.func,
  calculateButtonLabel: PropTypes.string,
  hideVisual: PropTypes.bool,
  condensed: PropTypes.bool,
  layout: PropTypes.oneOf(['balanced', 'results-focused', 'inputs-focused']),
  gridLayout: PropTypes.bool,
  className: PropTypes.string
};

export default CalculatorLayout;
